import type { ManualItineraryMoveResponse } from 'tripPlanner/api/types'
import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'
import { boundActivityDisplayText } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityDisplayText'
import { summarizeActivityItemNames } from 'tripPlanner/assistant/surfaceSnapshots/summarizeActivityItemNames'

type MoveSuccess = ManualItineraryMoveResponse['successes'][number]

function destinationLabel(to: MoveSuccess['to']): string {
  return to.dayLabel ?? 'Unplanned'
}

// A pure within-day/within-stop reorder (e.g. dragging an item to a different position on the
// same day) is a real `success` -- the mutation ran and the response reports it -- but it didn't
// actually change which stop/day the item is in, so reporting it via destinationLabel would claim
// a move that didn't happen. `from` is already on the response for exactly this reason; if the
// item's one prior placement matches `to`, it ended up back where it started.
//
// Only ever checked for a single-visit item (from.length === 1), not "any of them match" -- an
// item can legitimately have several prior placements (e.g. a transport item's DEPARTURE and
// ARRIVAL stop visits), and neither the move request nor response exposes which specific visit a
// given move targeted. Matching `to` against ANY of them would wrongly suppress a genuine move
// whenever it happens to land on a visit that didn't change (e.g. landing on the unchanged ARRIVAL
// point while the DEPARTURE point moved) -- silently dropping a real move from the log is worse
// than the (currently unreachable, since every direct move UI path already excludes multi-visit
// item types -- see canMoveTripItemToAnotherDay) risk of over-reporting one.
function isNoOpMove(success: MoveSuccess): boolean {
  return success.from.length === 1 && success.from.some(from => from.stopId === success.to.stopId && from.dayId === success.to.dayId)
}

// Built from the move response's own `successes`, never the request -- itemName/dayLabel only
// exist there, and the request only ever carries ids. moveManualItineraryItems is the only
// caller, and every move it makes (single-item drag, bulk list action) targets one shared
// destination, so every success here shares `to` with the rest -- destinationLabel(first.to)
// below relies on that.
// occurredAt is the caller's timestamp, not one taken here -- see buildItemDeletedActivityEntry
// for why (same delete-request-await shape; this one awaits the move request instead).
export function buildItemMovedActivityEntry(
  successes: ReadonlyArray<MoveSuccess>,
  sourcePath: 'trip_planner.itinerary.move',
  occurredAt: string,
): TripPlannerSurfaceActivityEntry | undefined {
  const movedSuccesses = successes.filter(success => !isNoOpMove(success))
  if (movedSuccesses.length === 0) return undefined

  const [first] = movedSuccesses
  const itemNames = movedSuccesses.map(success => success.itemName)

  return {
    kind: 'item_moved',
    label: movedSuccesses.length === 1 ? 'Moved itinerary item' : 'Moved itinerary items',
    displayText: boundActivityDisplayText(`Moved ${summarizeActivityItemNames(itemNames)} to ${destinationLabel(first.to)}`),
    sourcePath,
    dataClassification: 'personalised_surface',
    occurredAt,
  }
}
